import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Capture ?ref=CODE on first landing and persist for later signup attribution
export function captureReferralFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) {
      localStorage.setItem('tfu_ref_pending', ref.toUpperCase());
    }
  } catch (_) { /* noop */ }
}

export function getPendingReferralCode() {
  return localStorage.getItem('tfu_ref_pending') || null;
}

export function clearPendingReferralCode() {
  localStorage.removeItem('tfu_ref_pending');
}

const AuthContext = createContext(null);

const buildOauthUrl = (provider) => {
  const ref = getPendingReferralCode();
  const params = new URLSearchParams();
  params.set('origin', window.location.origin);
  if (ref) params.set('ref', ref);
  return `${API}/auth/${provider}/login?${params.toString()}`;
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signInOpen, setSignInOpen] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(res.data);
    } catch (_) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from legacy Emergent OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // Open the sign-in modal (preferred entry point from buttons everywhere)
  const openSignIn = useCallback(() => setSignInOpen(true), []);
  const closeSignIn = useCallback(() => setSignInOpen(false), []);

  // OAuth redirects
  const loginWithGoogle = useCallback(() => {
    window.location.href = buildOauthUrl('google');
  }, []);
  const loginWithGitHub = useCallback(() => {
    window.location.href = buildOauthUrl('github');
  }, []);

  // Email + password
  const signupWithEmail = useCallback(async ({ email, password, name, referral_code }) => {
    const res = await axios.post(
      `${API}/auth/signup`,
      { email, password, name, referral_code, origin_url: window.location.origin },
      { withCredentials: true }
    );
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const loginWithEmail = useCallback(async ({ email, password }) => {
    const res = await axios.post(
      `${API}/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    setUser(res.data.user);
    return res.data.user;
  }, []);

  // Magic link request (no immediate session — link is delivered by email)
  const requestMagicLink = useCallback(async ({ email, referral_code }) => {
    await axios.post(
      `${API}/auth/magic-link/request`,
      { email, referral_code, origin_url: window.location.origin },
      { withCredentials: true }
    );
  }, []);

  // Password reset
  const requestPasswordReset = useCallback(async ({ email }) => {
    await axios.post(
      `${API}/auth/forgot-password`,
      { email, origin_url: window.location.origin },
      { withCredentials: true }
    );
  }, []);

  const resetPassword = useCallback(async ({ token, new_password }) => {
    await axios.post(`${API}/auth/reset-password`, { token, new_password });
  }, []);

  // Legacy: kept so existing components that called `login()` still work.
  // Now it just opens the unified sign-in modal so users can pick their provider.
  const login = useCallback(() => setSignInOpen(true), []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (_) { /* ignore */ }
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        signInOpen,
        openSignIn,
        closeSignIn,
        login,
        logout,
        refresh: checkAuth,
        loginWithGoogle,
        loginWithGitHub,
        signupWithEmail,
        loginWithEmail,
        requestMagicLink,
        requestPasswordReset,
        resetPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
